'use client'

import { useCallback, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useStore } from '@/store'
import { clearClientAuthSession } from '@/lib/auth/clientSession'
import { AUTH_API_PATHS } from '@/lib/auth/paths'
import { AUTHENTICATION_FAILURE_EVENT } from '@/lib/auth/authFailureEvents'
import { AUTH_TAB_SYNC_KEY, parseAuthTabSyncEvent } from '@/lib/auth/tabSync'
import { getProtectedAuthArea } from '@/lib/auth/protectedArea'
import { shouldVerifyCustomerAuthOnMount } from '@/lib/auth/initialVerification'
import { resolveCustomerAuthBootstrap } from '@/lib/auth/customerAuthBootstrap'
import {
  classifyCustomerMeResponse,
  classifyCustomerVerifyResponse,
  type CustomerMeResult,
  type CustomerVerifyResult,
} from '@/lib/auth/verifyResponse'
import { fetchWithCloudflarePreclearance } from '@/lib/cloudflarePreclearance'

type AuthUser = {
  id: number
  firstName: string
  lastName: string
  email: string
  exp: number
}

type VerifyResult = CustomerVerifyResult<AuthUser>

type MeResult = CustomerMeResult<AuthUser>

let verifyInFlight: Promise<VerifyResult> | null = null
const VERIFY_TIMEOUT_MS = 10000
const TRANSIENT_AUTH_RETRY_DELAY_MS = 500

async function clearServerAuthSession(): Promise<void> {
  try {
    await fetch(AUTH_API_PATHS.CLEAR_SESSION, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    })
  } catch {
    // Best-effort cleanup before redirecting to login.
  }
}

async function retryTransientAuthOnce<
  T extends { success: boolean; authFailure?: boolean },
>(request: () => Promise<T>): Promise<T> {
  const first = await request()
  if (first.success || first.authFailure === true) {
    return first
  }

  await new Promise(resolve =>
    setTimeout(resolve, TRANSIENT_AUTH_RETRY_DELAY_MS)
  )
  return request()
}

export function AuthProvider({
  children,
  hasCustomerSessionHint = false,
}: {
  children: React.ReactNode
  hasCustomerSessionHint?: boolean
}) {
  const router = useRouter()
  const handlingAuthFailureRef = useRef(false)

  const redirectToProtectedLogin = useCallback(
    () => {
      if (typeof window === 'undefined') return

      const pathname = window.location.pathname || '/'
      const search = window.location.search || ''
      const redirectTarget = `${pathname}${search}`
      const params = new URLSearchParams()
      if (redirectTarget) {
        params.set('redirect', redirectTarget)
      }

      router.replace(
        `/account/login${params.toString() ? `?${params.toString()}` : ''}`
      )
    },
    [router]
  )

  const handleAuthenticationFailure = useCallback(
    async () => {
      if (typeof window === 'undefined' || handlingAuthFailureRef.current) {
        return
      }

      handlingAuthFailureRef.current = true

      clearClientAuthSession()
      await clearServerAuthSession()

      redirectToProtectedLogin()
    },
    [redirectToProtectedLogin]
  )

  const runVerify = useCallback((): Promise<VerifyResult> => {
    if (verifyInFlight) return verifyInFlight
    verifyInFlight = (async (): Promise<VerifyResult> => {
      const verifyOnce = async (): Promise<VerifyResult> => {
        const controller = new AbortController()
        const timeoutId = setTimeout(
          () => controller.abort(),
          VERIFY_TIMEOUT_MS
        )
        try {
          const response = await fetchWithCloudflarePreclearance(
            AUTH_API_PATHS.VERIFY,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              signal: controller.signal,
            }
          )
          return await classifyCustomerVerifyResponse<AuthUser>(response)
        } catch {
          return { success: false, authFailure: false }
        } finally {
          clearTimeout(timeoutId)
        }
      }

      const result = await retryTransientAuthOnce(verifyOnce)
      verifyInFlight = null
      return result
    })()
    return verifyInFlight
  }, [])

  const runMe = useCallback((): Promise<MeResult> => {
    return fetchWithCloudflarePreclearance(AUTH_API_PATHS.ME, { method: 'GET' })
      .then(response => classifyCustomerMeResponse<AuthUser>(response))
      .catch(() => ({ success: false as const, authFailure: false }))
  }, [])

  const applyVerifiedCustomerSession = useCallback(
    async (result: { user?: AuthUser | null }) => {
      handlingAuthFailureRef.current = false
      if (result.user) {
        useStore.getState().setUser(result.user)
        return
      }

      const me = await runMe()
      if (me.success && me.user) {
        useStore.getState().setUser(me.user)
        return
      }

      if (!me.success && me.authFailure) {
        const area = getProtectedAuthArea(window.location.pathname || '/')
        if (area === 'account') {
          await handleAuthenticationFailure()
        } else {
          clearClientAuthSession()
        }
      }
    },
    [handleAuthenticationFailure, runMe]
  )

  const handleCustomerSignedOutElsewhere = useCallback(() => {
    handlingAuthFailureRef.current = false
    clearClientAuthSession()

    const pathname = window.location.pathname || '/'
    const area = getProtectedAuthArea(pathname)
    if (area === 'account') {
      redirectToProtectedLogin()
      return
    }

    router.refresh()
  }, [redirectToProtectedLogin, router])

  const syncCustomerSignedInElsewhere = useCallback(async () => {
    const result = await runVerify()
    if (!result.success) {
      return
    }

    await applyVerifiedCustomerSession(result)
    router.refresh()
  }, [applyVerifiedCustomerSession, router, runVerify])

  useEffect(() => {
    const pathname = window.location.pathname || '/'
    const hasPersistedUser = Boolean(useStore.getState().user)
    const shouldVerify = shouldVerifyCustomerAuthOnMount({
      pathname,
      hasCustomerSessionHint,
      hasPersistedUser,
    })

    if (!shouldVerify) {
      useStore.getState().setAuthResolved(true)
    } else {
      void resolveCustomerAuthBootstrap({
        verify: runVerify,
        applyVerifiedSession: applyVerifiedCustomerSession,
        handleAuthFailure: async () => {
          useStore.getState().setUser(null)

          const pathname = window.location.pathname || '/'
          const area = getProtectedAuthArea(pathname)
          if (area === 'account') {
            await handleAuthenticationFailure()
          } else if (pathname.startsWith('/account')) {
            clearClientAuthSession()
          }
        },
        markResolved: () => {
          useStore.getState().setAuthResolved(true)
        },
      })
    }

    const handleAuthenticationFailureEvent = () => {
      const area = getProtectedAuthArea(window.location.pathname || '/')
      if (!area) {
        useStore.getState().setUser(null)
        return
      }

      void handleAuthenticationFailure()
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_TAB_SYNC_KEY) return

      const authEvent = parseAuthTabSyncEvent(event.newValue)
      if (!authEvent || authEvent.scope !== 'customer') return

      if (authEvent.action === 'signin') {
        void syncCustomerSignedInElsewhere()
        return
      }

      handleCustomerSignedOutElsewhere()
    }

    window.addEventListener(
      AUTHENTICATION_FAILURE_EVENT,
      handleAuthenticationFailureEvent
    )
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener(
        AUTHENTICATION_FAILURE_EVENT,
        handleAuthenticationFailureEvent
      )
      window.removeEventListener('storage', handleStorage)
    }
  }, [
    applyVerifiedCustomerSession,
    hasCustomerSessionHint,
    handleAuthenticationFailure,
    handleCustomerSignedOutElsewhere,
    runVerify,
    syncCustomerSignedInElsewhere,
  ])

  return <>{children}</>
}
